import React, { useEffect, useState } from "react";
import { Server, Globe, ShieldCheck, Fingerprint, Network } from "lucide-react";
import api from "lib/api";
import { flagEmoji } from "lib/country-centroids";

function Card({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="h-4 w-4 text-primary" /> {label}
      </div>
      <div className="mt-2 text-2xl font-semibold">{(value ?? 0).toLocaleString("pt-BR")}</div>
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/stats").then((r) => setS(r.data)).catch((e) =>
      setError(e.response?.data?.detail || "Falha ao carregar estatísticas (conexão OpenSearch cadastrada?)")
    );
  }, []);

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!s) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Dashboard</h1>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <Card icon={Network} label="Hosts únicos" value={s.unique_hosts} />
        <Card icon={Server} label="Serviços" value={s.services} />
        <Card icon={Globe} label="Registros DNS" value={s.dns_records} />
        <Card icon={ShieldCheck} label="Obs. de cert" value={s.cert_observations} />
        <Card icon={Fingerprint} label="Certs únicos" value={s.unique_certs} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 text-sm font-semibold">Top portas</div>
          {s.top_ports?.map((p) => (
            <div key={p.port} className="flex justify-between border-b border-border py-1 text-sm">
              <span className="font-mono">{p.port}</span>
              <span className="text-muted-foreground">{p.count.toLocaleString("pt-BR")}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 text-sm font-semibold">Top produtos</div>
          {s.top_products?.map((p) => (
            <div key={p.product} className="flex justify-between border-b border-border py-1 text-sm">
              <span>{p.product}</span>
              <span className="text-muted-foreground">{p.count.toLocaleString("pt-BR")}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 text-sm font-semibold">Top países</div>
          {(s.top_countries?.length ? s.top_countries : null)?.map((c) => (
            <div key={c.country} className="flex justify-between border-b border-border py-1 text-sm">
              <span className="flex items-center gap-2">
                <span>{flagEmoji(c.country)}</span>
                <span>{(c.country || "—").toUpperCase()}</span>
              </span>
              <span className="text-muted-foreground">{c.count.toLocaleString("pt-BR")}</span>
            </div>
          )) || <div className="text-sm text-muted-foreground">sem dados</div>}
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 text-sm font-semibold">Top ASN</div>
          {(s.top_asn?.length ? s.top_asn : null)?.map((a) => (
            <div key={a.asn} className="flex items-center justify-between gap-3 border-b border-border py-1 text-sm">
              <span className="min-w-0 truncate">
                <span className="font-mono">AS{a.asn}</span>
                {a.org && <span className="text-muted-foreground"> · {a.org}</span>}
              </span>
              <span className="shrink-0 text-muted-foreground">{a.count.toLocaleString("pt-BR")}</span>
            </div>
          )) || <div className="text-sm text-muted-foreground">sem dados (reimporte p/ popular o ASN)</div>}
        </div>
      </div>
    </div>
  );
}
